import { Grid, Typography } from '@material-ui/core';
import { filter as _filter, orderBy as _orderBy } from 'lodash';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CategorySearchSortType } from 'src/common/enumerations/category-search-sort-type';
import { getTransactionListings, ITransactionListing } from 'src/common/helpers/transaction-helper';
import { popupMessage } from 'src/messages/business-logic/popup-message';
import SearchTransactionsBottomControl from './components/search-transactions-bottom-control/search-transactions-bottom-control';
import SortButton from './components/sort-button/sort-button';
import TransactionList from './components/transaction-list/transaction-list';
import { styles } from './search-transactions-styles';

interface ISearchTransactionProps {
  searchText: string;
  onSubmit: Function;
  setTotalCount: Function;
  submitButtonText: string;
}

const SearchTransactions: React.FC<ISearchTransactionProps> = ({
  searchText,
  onSubmit,
  setTotalCount,
  submitButtonText,
}: ISearchTransactionProps): JSX.Element => {
  const classes = styles();
  const { t } = useTranslation();
  const [transactionListings, setTransactionListings] = useState<ITransactionListing[]>([]);
  const [filteredTransactions, setFilteredTransactions] = useState<ITransactionListing[]>([]);
  const [selectedTransactionID, setSelectedTransactionID] = useState<string>('');
  const [selectedTouchpoiontId, setSelectedTouchpointId] = useState<string>('');
  const [selectedSort, setSelectedSort] = useState<CategorySearchSortType>(
    CategorySearchSortType.SequenceNumber,
  );
  const [submitBtnDisabled, setSubmitBtnDisabled] = useState<boolean>(true);

  useEffect(() => {
    getTransactionListings()
      .then(setTransactionListings)
      .catch(popupMessage);
  }, []);

  useEffect(() => {
    let trans = [...transactionListings];

    trans = filterTransactionsByTPID(trans, searchText);

    switch (selectedSort) {
      case CategorySearchSortType.SequenceNumber:
        trans = _orderBy(trans, t => Number(t.sequenceNumber));
        break;
      case CategorySearchSortType.POS:
        trans = _orderBy(trans, ['touchPointId']);
        break;
      case CategorySearchSortType.PriceLowest:
        trans = _orderBy(trans, t => Number(t.totalOrderAmount));
        break;
      case CategorySearchSortType.LastDay:
        trans = filterTransactionsForElapsedDays(trans, 1);
        break;
      case CategorySearchSortType.LastWeek:
        trans = filterTransactionsForElapsedDays(trans, 7);
        break;
      case CategorySearchSortType.LastMonth:
        trans = filterTransactionsForElapsedDays(trans, 30);
        break;
    }
    /* clear selected transaction if user begins to search */
    setSelectedTransactionID('');
    /* disable the recall transaction button too */
    setSubmitBtnDisabled(true);
    setFilteredTransactions(trans);
    setTotalCount(trans.length);
  }, [transactionListings, searchText, selectedSort, setTotalCount]);

  const onTransactionSelected = (transaction: ITransactionListing) => {
    if (!selectedTransactionID) {
      setSelectedTransactionID(transaction.transactionId);
      setSelectedTouchpointId(transaction.touchPointId);
      setSubmitBtnDisabled(false);
    } else {
      if (selectedTransactionID === transaction.transactionId) {
        setSelectedTransactionID('');
        setSelectedTouchpointId('');
        setSubmitBtnDisabled(true);
      } else {
        setSelectedTransactionID(transaction.transactionId);
        setSelectedTouchpointId(transaction.touchPointId);
        setSubmitBtnDisabled(false);
      }
    }
  };

  return (
    <Grid
      id='search-transactions'
      container
      direction='column'
      justifyContent='space-between'
      className={`fullHeight ${classes.component}`}
    >
      <Grid item className={classes.sortFilters}>
        <Typography variant='caption' className={classes.categoryLabel}>
          {t('SortedBy')}
        </Typography>
        <Grid container>
          <SortButton
            title='Transaction ID'
            selected={selectedSort === CategorySearchSortType.SequenceNumber}
            onClick={() => setSelectedSort(CategorySearchSortType.SequenceNumber)}
            testId='sort-button-transid'
          />
          <SortButton
            title='POS'
            selected={selectedSort === CategorySearchSortType.POS}
            onClick={() => setSelectedSort(CategorySearchSortType.POS)}
            testId='sort-button-pos'
          />
          <SortButton
            title='Last Day'
            selected={selectedSort === CategorySearchSortType.LastDay}
            onClick={() => setSelectedSort(CategorySearchSortType.LastDay)}
            testId='sort-button-day'
          />
          <SortButton
            title='Last Week'
            selected={selectedSort === CategorySearchSortType.LastWeek}
            onClick={() => setSelectedSort(CategorySearchSortType.LastWeek)}
            testId='sort-button-week'
          />
          <SortButton
            title='Last Month'
            selected={selectedSort === CategorySearchSortType.LastMonth}
            onClick={() => setSelectedSort(CategorySearchSortType.LastMonth)}
            testId='sort-button-month'
          />
          <SortButton
            title='Price: Low to High'
            selected={selectedSort === CategorySearchSortType.PriceLowest}
            onClick={() => setSelectedSort(CategorySearchSortType.PriceLowest)}
            testId='sort-button-price'
          />
          {/* <SortButton
            title='Alphabetical: A to Z'
            selected={tab === CategorySearchSortType.AlphabeticalAsc}
            onClick={() => setTab(CategorySearchSortType.AlphabeticalAsc)}
          /> */}
        </Grid>
      </Grid>
      <Grid item container className={`flexFull fullWidth ${classes.transactions}`}>
        <TransactionList
          transactions={filteredTransactions}
          selectedTransactionID={selectedTransactionID}
          onTransactionClick={onTransactionSelected}
        />
      </Grid>
      <Grid item className={classes.controls}>
        <SearchTransactionsBottomControl
          submitButton={{
            label: t(submitButtonText),
            onClick: () => onSubmit(selectedTransactionID, selectedTouchpoiontId),
            disabled: submitBtnDisabled,
          }}
        />
      </Grid>
    </Grid>
  );
};

export default SearchTransactions;

const filterTransactionsByTPID = (transactions: ITransactionListing[], tpID: string) => {
  let trans = [...transactions];

  trans = _filter(trans, t => {
    return t.touchPointId.includes(tpID);
  });

  return trans;
};

const filterTransactionsForElapsedDays = (
  transactions: ITransactionListing[],
  ellapsedDays: number,
) => {
  let trans = [...transactions];

  trans = _filter(trans, t => {
    const days = moment().diff(t.suspendTime, 'days');
    return days < ellapsedDays;
  });

  return trans;
};
